import React, { useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { API_URL } from "./config";
import { AppProvider } from "./context/AppContext";
import ToolsMenu from "./components/ToolsMenu";
import PruebasMenu from "./components/PruebasMenu";
import NewToolsMenu from "./components/NewToolsMenu";
import NavItem from "./components/NavItem";
import UserMenu from "./components/UserMenu";
import Login from "./components/Login";
import Biblioteca from "./components/Biblioteca";
import Videoteca from "./components/Videoteca";
import Certificar from "./components/Certificar";
import PromptIn from "./pages/PromptIn";
import MdvPage from "./pages/MdvPage";
import Generar from "./pages/Generar";
import Characters from "./pages/Characters";
import CharacterDesign from "./pages/CharacterDesign";
import Wardrobe from "./pages/Wardrobe";
import SceneComposer from "./pages/SceneComposer";
import VideoGenerator from "./pages/VideoGenerator";
import VideoSet from "./pages/VideoSet";
import EditorKeyFrame from "./pages/EditorKeyFrame";
import ImageComposer from "./pages/ImageComposer";
import GeneradorLab from "./pages/GeneradorLab";
import FaceMaker from "./pages/FaceMaker";
import FaceComposer from "./pages/FaceComposer";
import StudioCharacter from "./pages/StudioCharacter";
import StudioDetails from "./pages/StudioDetails";
import StudioCharacterSheet from "./pages/StudioCharacterSheet";
import StudioScene from "./pages/StudioScene";
import StudioShot from "./pages/StudioShot";
import StudioVideo from "./pages/StudioVideo";
import AdminUsers from "./pages/AdminUsers";
import AdminRoles from "./pages/AdminRoles";
import Home from "./pages/Home";
import logo from "./assets/logo_720medialabs.png";
import "./App.css";

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
}

function getStoredPerms() {
  try {
    const raw = JSON.parse(localStorage.getItem("perms"));
    return raw ? new Set(raw) : new Set();
  } catch { return new Set(); }
}

function getToken() {
  return localStorage.getItem("token") || "";
}

// ---------------------------------------------------------------------------
// Unified permission helper
// ---------------------------------------------------------------------------
function makeCan(permSet) {
  return (perm) => {
    if (permSet.has("*")) return true;
    if (permSet.has(perm)) return true;
    for (const p of permSet) {
      if (p.endsWith(".*") && perm.startsWith(p.slice(0, -1))) return true;
    }
    return false;
  };
}

function MainLayout({ user, token, health, onLogout, can }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="app">
      <header className="topbar">
        <Link to="/home" className="topbar-logo-wrap" style={{textDecoration: "none"}}>
          <img src={logo} alt="720 Media Labs" className="topbar-logo-img" />
          <span className="topbar-version">v1.36</span>
        </Link>
        <ToolsMenu can={can} />
        {can("studio.character") && <NewToolsMenu />}
        {can("tools.editor") && <PruebasMenu />}
        <nav className={`nav ${menuOpen ? "nav--open" : ""}`}>
          {can("biblioteca")   && <NavItem to="/biblioteca" label="Biblioteca" onClick={() => setMenuOpen(false)} />}
          {can("videoteca")    && <NavItem to="/videoteca"  label="Videoteca"  onClick={() => setMenuOpen(false)} />}
          {can("certificar")   && <NavItem to="/certificar" label="Certificar" onClick={() => setMenuOpen(false)} />}
          {can("admin.users")  && <NavItem to="/admin/users" label="Usuarios" onClick={() => setMenuOpen(false)} />}
          {can("admin.roles")  && <NavItem to="/admin/roles" label="Roles" onClick={() => setMenuOpen(false)} />}
        </nav>
        <button className="topbar-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? "✕" : "☰"}
        </button>
        <span className="topbar-sep topbar-sep--desktop" />
        {user && <UserMenu user={user} onLogout={onLogout} />}
        <span className="topbar-sep topbar-sep--desktop" />
        <div className="topbar-status topbar-status--desktop">
          <span className={`status-dot ${health.backend ? "ok" : "err"}`} />
          <span className="status-label">Backend</span>
          <span className={`status-dot ${health.comfyui ? "ok" : "err"}`} />
          <span className="status-label">ComfyUI</span>
          <span className={`status-dot ${health.llm ? "ok" : "err"}`} />
          <span className="status-label">LLM</span>
        </div>
      </header>

      <div className="main-layout">
        <Routes>
          {/* Key Frames */}
          {can("tools.keyframes") && <Route path="/keyframes/prompt-in" element={<PromptIn />} />}
          {can("tools.mdv")       && <Route path="/keyframes/mdv"       element={<MdvPage />} />}
          {can("tools.keyframes") && <Route path="/keyframes/prompter"  element={<Generar />} />}
          {can("tools.editor")    && <Route path="/keyframes/editor"    element={<EditorKeyFrame />} />}
          {can("tools.editor")    && <Route path="/keyframes/image-composer" element={<ImageComposer />} />}
          {can("tools.editor")    && <Route path="/keyframes/lab" element={<GeneradorLab />} />}
          {can("tools.editor")    && <Route path="/keyframes/facemaker" element={<FaceMaker />} />}
          {can("tools.editor")    && <Route path="/keyframes/face-composer" element={<FaceComposer />} />}

          {/* Character Sheets */}
          {can("tools.characters") && <Route path="/characters"       element={<Characters />} />}
          {can("tools.characters") && <Route path="/character-design" element={<CharacterDesign />} />}
          {can("tools.wardrobe")   && <Route path="/wardrobe"         element={<Wardrobe />} />}
          {can("tools.scenes")     && <Route path="/scenes"           element={<SceneComposer />} />}
          {can("tools.video")      && <Route path="/video"            element={<VideoGenerator />} />}
          {can("tools.videoset")   && <Route path="/video-set"        element={<VideoSet />} />}

          {/* Studio */}
          {can("studio.character")       && <Route path="/studio/character"       element={<StudioCharacter />} />}
          {can("studio.details")         && <Route path="/studio/details"         element={<StudioDetails />} />}
          {can("studio.character-sheet") && <Route path="/studio/character-sheet" element={<StudioCharacterSheet />} />}
          {can("studio.scene")           && <Route path="/studio/scene"           element={<StudioScene />} />}
          {can("studio.shot")            && <Route path="/studio/shot"            element={<StudioShot />} />}
          {can("studio.video")           && <Route path="/studio/video"           element={<StudioVideo />} />}

          {/* Home */}
          <Route path="/home" element={<Home can={can} />} />

          {/* Shared */}
          {can("biblioteca")  && <Route path="/biblioteca" element={<Biblioteca token={token} />} />}
          {can("videoteca")   && <Route path="/videoteca"  element={<Videoteca token={token} />} />}
          {can("certificar")  && <Route path="/certificar" element={<Certificar token={token} />} />}
          {can("admin.users") && <Route path="/admin/users" element={<AdminUsers />} />}
          {can("admin.roles") && <Route path="/admin/roles" element={<AdminRoles />} />}

          {/* Legacy routes → redirect */}
          <Route path="/prompt-in" element={<Navigate to="/keyframes/prompt-in" />} />
          <Route path="/prompter"  element={<Navigate to="/keyframes/prompter" />} />
          <Route path="/generar"   element={<Navigate to="/keyframes/prompter" />} />

          {/* Default */}
          <Route path="/"  element={<Navigate to="/home" />} />
          <Route path="*"  element={<Navigate to="/home" />} />
        </Routes>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser]     = useState(getStoredUser);
  const [health, setHealth] = useState({ backend: false, comfyui: false, llm: false, model_ckpt: "" });
  const [perms, setPerms]   = useState(getStoredPerms);
  const [authLoading, setAuthLoading] = useState(false);
  const abortRef = useRef(null);

  // Fetch user + permissions atomically, with AbortController.
  // Returns true if permissions were loaded successfully.
  const refreshAuth = useCallback(async (token) => {
    if (!token) return false;
    // Cancel any in-flight auth request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, permRes] = await Promise.all([
        fetch(`${API_URL}/auth/me`, { headers, signal: controller.signal }),
        fetch(`${API_URL}/auth/permissions`, { headers, signal: controller.signal }),
      ]);
      if (controller.signal.aborted) return false;
      if (meRes.status === 401 || meRes.status === 403) {
        if (token === localStorage.getItem("token")) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("perms");
          setUser(null);
          setPerms(new Set());
        }
        return false;
      }
      // Update user and perms atomically
      if (meRes.ok) {
        const fresh = await meRes.json();
        localStorage.setItem("user", JSON.stringify(fresh));
        setUser(fresh);
      }
      if (permRes.ok) {
        const data = await permRes.json();
        const permList = data.permissions || [];
        localStorage.setItem("perms", JSON.stringify(permList));
        setPerms(new Set(permList));
        return true;
      }
      return false;
    } catch (e) {
      if (e.name === "AbortError") return false;
      console.error("refreshAuth error:", e);
      return false;
    }
  }, []);

  // On mount: validate stored token only if we have a stored user
  useEffect(() => {
    const token = getToken();
    const storedUser = getStoredUser();
    if (token && storedUser) {
      const storedPerms = getStoredPerms();
      // If we have no cached perms, show loading while we fetch them
      if (storedPerms.size === 0 && storedUser.role !== "owner") {
        setAuthLoading(true);
        refreshAuth(token).finally(() => setAuthLoading(false));
      } else {
        refreshAuth(token);
      }
    }
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [refreshAuth]);

  // Health polling
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/health`);
        if (res.ok) setHealth(await res.json());
        else setHealth({ backend: true, comfyui: false, llm: false, model_ckpt: "" });
      } catch {
        setHealth({ backend: false, comfyui: false, llm: false, model_ckpt: "" });
      }
    };
    check();
    const interval = setInterval(check, 10000);
    return () => clearInterval(interval);
  }, []);

  const can = useCallback(makeCan(perms), [perms]);

  const handleLogin = async (userData) => {
    setUser(userData);
    setAuthLoading(true);
    try {
      const token = localStorage.getItem("token") || "";
      if (token) {
        const ok = await refreshAuth(token);
        if (!ok) {
          // refreshAuth failed — use role from login response as fallback
          const role = userData.role || "guest";
          if (role === "owner") {
            setPerms(new Set(["*"]));
            localStorage.setItem("perms", JSON.stringify(["*"]));
          } else {
            // Fetch permissions one more time as standalone retry
            try {
              const res = await fetch(`${API_URL}/auth/permissions`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (res.ok) {
                const data = await res.json();
                const permList = data.permissions || [];
                localStorage.setItem("perms", JSON.stringify(permList));
                setPerms(new Set(permList));
              }
            } catch { /* retry failed, perms stay as stored */ }
          }
        }
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    if (abortRef.current) abortRef.current.abort();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("perms");
    localStorage.removeItem("720mt_session");
    setUser(null);
    setPerms(new Set());
  };

  if (!user) {
    return (
      <BrowserRouter>
        <Login onLogin={handleLogin} />
      </BrowserRouter>
    );
  }

  if (authLoading) {
    return (
      <div className="login-bg" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#fff" }}>
          <div className="auth-spinner" />
          <p style={{ marginTop: 16, fontSize: 14, opacity: 0.8 }}>Cargando permisos…</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AppProvider>
        <MainLayout user={user} token={getToken()} health={health} onLogout={handleLogout} can={can} />
      </AppProvider>
    </BrowserRouter>
  );
}
